/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import {
    Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { Layer } from "../../../../actions/Layers/constants";
import useConfig from "../../../../actions/Tenants/config/configHook";
import apiClient from "../../../../constants/apiClient";
import { ROOT_URL } from "../../../../constants/urls";
import { useConnectivity } from "../../../../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../../../../offline/db";
import { startOfflineCheck } from "../../../../offline/startOfflineCheck";
import { isFilledArray, matchesPattern } from "../../../../utils";
import { toast } from "../../../../utils/toast";
import useNavigateFrom from "../../../../utils/useNavigateFrom";
import LayerAutoComplete from "../../../Forms/LayerAutoComplete";
import QRCodeScan from "../../../Forms/QRCodeScan";
import { RadioButtons } from "../../../Forms/RadioButton";
import { Loading } from "../../../Helper/Loading";
import { ExpandableContainer } from "../../Helper/ExpandableContainer";

interface CreateLayerProps {
    modal?: boolean
    isOpen?: { withScanner: boolean} | false;
    onClose?: () => void;
    onSubmit: () => void;
    disableSaveButton: boolean;
    setValue: (field: string, value: any) => void;
    layer: Layer;
    setLayerSelect: (layer: Layer | Layer[], type?: string) => void;
    layerConfig?: any;
    initialCheckType?: string
  }

export function CreateLayer({
    onClose, onSubmit, isOpen, disableSaveButton, setValue, layer, setLayerSelect, modal, layerConfig, initialCheckType
}: CreateLayerProps) {
    const { navigateTo } = useNavigateFrom();

    const [showFormNotScanner, setShowFormNotScanner] = useState(false);
    const [searchingForScannedLabel, setSearchingForScannedLabel] = useState(false);
    const [checkType, setCheckType] = useState(initialCheckType);
    const [showAllCheckButtons, setShowAllCheckButtons] = useState(false);

    const config = useConfig();
    const barcodeScannerMetaFallback = config.root_config.barcode_scanner_meta_fallback;
    const { isOnline } = useConnectivity();
    const offlineFeatureEnabled = isOfflineChecksEnabled(config);
    // Effective offline mode: the tenant has the master flag on AND
    // connectivity is currently down. When the flag is off, we never
    // take the offline branch (just like every other Phase 5 site).
    const inOfflineMode = offlineFeatureEnabled && !isOnline;
    const { label_format, invalid_hint } = layerConfig ?? {};
    // When creating layers...check if the label matches the format
    const labelFormatInvalid = label_format && !matchesPattern(layer?.label, label_format);

    useEffect(() => {
        // * Reset the label field only when closed
        // * Dont remove this, as isOpen changes when you use the dropdown box
        if (isOpen) {
            return;
        }

        setValue("label", "");
        setSearchingForScannedLabel(false);
        setShowFormNotScanner(false);
        setShowAllCheckButtons(false);
    }, [isOpen]);

    const handleScan = (result) => {
        if (inOfflineMode) {
            goToScannedLayerOffline(result);
        } else {
            goToScannedLayer(result);
        }
        setValue("label", result);
    };

    /**
     * Save click handler.
     *
     * Online: hands off to the parent's onSubmit (Redux thunk that POSTs
     * to /api/layers/ to create or look up the layer).
     *
     * Offline: that POST fails with ERR_INTERNET_DISCONNECTED and leaves
     * the modal stuck. Route through goToScannedLayerOffline instead —
     * it resolves the typed label against the offline pallet cache,
     * creates an OfflineCheckSession, and navigates to the start-check
     * route. Same code path the camera scanner uses (handleScan above),
     * just triggered by Save instead of an onScan event.
     *
     * Worth noting: this means the offline modal can never *create* a
     * brand-new layer — only resolve an existing cached one. That's the
     * intended semantics (you can't push new entities to the server
     * while offline; the design says scan-only).
     */
    const handleSave = () => {
        if (inOfflineMode && layer.label) {
            goToScannedLayerOffline(layer.label);
        } else {
            onSubmit();
        }
    };

    const retryScanner = () => {
        setShowFormNotScanner(false);
        setValue("label", "");
    };

    const goToScannedLayer = (label) => {
        setSearchingForScannedLabel(true);

        apiClient
            .get(`${ROOT_URL}api/layers/search-label/`, {
                params: {
                    label,
                    meta_keys: isFilledArray(barcodeScannerMetaFallback) ? barcodeScannerMetaFallback.join(",") : undefined,
                }
            }).then((response) => {
                if (response.data.layer_id) {
                    const check = { location: checkType || "" };
                    const location = config.get_check_type_config(response.data, check);

                    if (location) {
                        // Keep the originating `from` (e.g. ripening room) alive across the pallet switch.
                        navigateTo(`/layer/${response.data.layer_id}/action/start-${checkType}`, true);
                    } else {
                        navigateTo(`/layer/${response.data.layer_id}`, true);
                    }
                }

                setSearchingForScannedLabel(false);
            }).catch((error) => {
                // eslint-disable-next-line no-console
                console.log(error);
                toast.error(`Could not search for ${label}`);
                setSearchingForScannedLabel(false);
            });
    };

    /**
     * Offline barcode-scan path. Delegates session creation to the
     * shared `startOfflineCheck` helper so the camera-scan, typed-Save,
     * and autocomplete-pick paths all funnel through the same logic
     * (lookup against offline-layer-cache → fetch configHash from
     * etag-cache → createSession → setActiveSessionId). On success
     * navigates to the same start-check route the online path uses;
     * Phase 5.3 picks the session up from there and renders the form
     * from synthetic Redux state instead of dispatching createCheck.
     */
    const goToScannedLayerOffline = async (label) => {
        setSearchingForScannedLabel(true);
        try {
            const result = await startOfflineCheck({
                label,
                checkType,
                tenantId: config.tenant_id,
            });
            if (!result.ok) {
                toast.error(result.reason);
                return;
            }
            // Keep the originating `from` (e.g. ripening room) alive across the pallet switch.
            navigateTo(`/layer/${result.layerId}/action/start-${checkType}`, true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log("[offline] scan failed", err);
            toast.error(`Could not search for ${label}`);
        } finally {
            setSearchingForScannedLabel(false);
        }
    };

    const checks = useMemo(() => {
        const baseChecks = config?.check_locations?.filter((item) => item.show_in_barcode_scanner_shortcut) ?? [];
        // When operating offline, hide check types that aren't explicitly
        // marked offline_enabled (issue 2.6). Strict equality with `true`
        // — both `false` and absence mean "not opted in", so no check
        // type silently becomes offline-available. This matches the
        // backend's interpretation in §3.11.2.
        if (inOfflineMode) {
            return baseChecks.filter((ct) => (ct as { offline_enabled?: unknown }).offline_enabled === true);
        }
        return baseChecks;
    }, [config?.check_locations, inOfflineMode]);

    const creatableLayerTypes = useMemo(() => {
        return config.layer_types && config.layer_types.filter((i) => i.is_creatable !== false);
    }, [config?.layer_types, layer]);

    const isScannerOpen = useMemo(() => {
        return (!modal || (isOpen && isOpen?.withScanner)) && config.use_barcode_scanner && !showFormNotScanner && !layer.label && !searchingForScannedLabel;
    }, [config.use_barcode_scanner, isOpen, layer?.label, searchingForScannedLabel, showFormNotScanner, modal]);

    const title = "Scan pallet";
    const body = <>
        {!!checks.length && (
            <>
                <FormGroup>
                    <Label>Check type</Label>

                    <ExpandableContainer
                        selector=".check-type-button"
                        isExpanded={showAllCheckButtons}
                        trigger={
                            <div className="mt-2 pt-2 border-top">
                                <Button
                                    onClick={() => setShowAllCheckButtons(!showAllCheckButtons)}
                                    className="text-nowrap text-reset px-0"
                                    color="link"
                                    size="sm"

                                >
                                    {showAllCheckButtons ? "Show less" : `Show ${checks.length - 3} more`}
                                </Button>
                            </div>
                        }
                    >
                        <RadioButtons
                            color="primary"
                            name="checkType"
                            current={checkType}
                            options={checks}
                            onChange={(_, value) => setCheckType(value)}
                            className="check-type-button"
                        />
                    </ExpandableContainer>
                </FormGroup>
            </>
        )}

        {searchingForScannedLabel && <Loading />}

        {isScannerOpen && (
            <QRCodeScan
                onScan={handleScan}
                barcode_filter={config.barcode_filter}
            />
        )}

        {!isScannerOpen && isFilledArray(creatableLayerTypes) && (
            <FormGroup>
                <Label>Type</Label>

                <RadioButtons
                    color="primary"
                    name="type"
                    current={layer.type!}
                    options={creatableLayerTypes}
                    onChange={setValue}
                />
            </FormGroup>
        )}

        {!isScannerOpen && (
            <LayerAutoComplete
                multi={false}
                type={layer.type}
                layer={layer}
                setLayer={(selected) => setLayerSelect(selected, checkType)}
                label_format={label_format}
                invalid_hint={invalid_hint}
                creatable={layerConfig?.is_creatable !== false}
            />
        )}

        <div className="w-100">
            {isScannerOpen ? (
                <Button color="secondary" outline onClick={() => setShowFormNotScanner(true)} className="w-100" key="manual-label-button">
                        Enter Manually
                </Button>
            ) : (
                <>
                    {modal ? (
                        <Button color="secondary" outline onClick={retryScanner} className="w-100" key="scanner-button">
                                Retry Scan
                        </Button>
                    ) : (
                        <Button disabled={disableSaveButton || labelFormatInvalid} color="primary" onClick={handleSave} className="w-100">
                                Save
                        </Button>
                    )}
                </>

            )}
        </div>
    </>;


    const footer = <div css={modal && css`margin-top: 48px; > * { min-width: 100px; }`} className="d-flex gap-2 justify-content-end w-100">
        {!isScannerOpen && !modal && (
            <Button color="secondary" outline onClick={retryScanner} className="w-100" key="scanner-button">
                    Retry Scan
            </Button>
        )}

        {modal && <Button color="secondary" onClick={onClose}>Cancel</Button>}

        {!isScannerOpen && modal && (
            <Button disabled={disableSaveButton || labelFormatInvalid} color="primary" onClick={handleSave}>
                    Save
            </Button>
        )}
    </div>;

    return modal ? (
        <Modal isOpen={!!isOpen} size="md" centered>
            <ModalHeader toggle={onClose} onClick={onClose}>
                {title}
            </ModalHeader>

            <ModalBody className="d-flex flex-column gap-2">
                {body}
            </ModalBody>

            <ModalFooter>
                {footer}
            </ModalFooter>
        </Modal>
    ) : (
        <div className="d-flex flex-column gap-2 mx-auto" css={css`max-width: 440px;`}>
            <h1 className="text-align-center mb-4">{title}</h1>

            <div className="d-flex flex-column gap-2">
                {body}

                <div className="d-flex w-100">
                    {footer}
                </div>
            </div>
        </div>
    );
}
